import { useRef, useState, type ReactElement, type ReactNode } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

export interface TooltipProps {
  content: ReactNode;
  /** A single element that accepts a ref (any DOM element does). */
  children: ReactElement;
  side?: 'top' | 'bottom' | 'left' | 'right';
  align?: 'start' | 'center' | 'end';
  /** Open delay in ms. Default 300. */
  delay?: number;
  /** Only show the tooltip when the trigger's content is actually truncated
   *  (scrollWidth > clientWidth) — e.g. a `truncate` cell. */
  whenTruncated?: boolean;
  /** Default 280. */
  maxWidth?: number;
}

export default function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  delay = 300,
  whenTruncated = false,
  maxWidth = 280,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleOpenChange = (next: boolean) => {
    if (next && whenTruncated) {
      // Measure at open time so resizes are always respected. If the child
      // isn't truncated, the tooltip simply never opens — the child renders
      // as-is with no overlay.
      const el = triggerRef.current;
      if (el && el.scrollWidth <= el.clientWidth) return;
    }
    setOpen(next);
  };

  return (
    <TooltipPrimitive.Provider delayDuration={delay}>
      <TooltipPrimitive.Root open={open} onOpenChange={handleOpenChange}>
        <TooltipPrimitive.Trigger asChild ref={triggerRef}>
          {children}
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            side={side}
            align={align}
            sideOffset={8}
            collisionPadding={8}
            className="z-(--z-dropdown) rounded-control bg-ink px-2 py-1 text-caption text-cream-light shadow-overlay animate-pop"
            style={{ maxWidth }}
          >
            {content}
            <TooltipPrimitive.Arrow className="fill-ink" width={10} height={5} />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
